import { useEffect, useState } from 'react';
import { InboxOutlined } from '@ant-design/icons';
import { Alert, Form, Spin, Typography, Upload } from 'antd';
import type { RcFile } from 'antd/lib/upload';
import { DataFeedFormField, JSONRequest } from './DataFeedForm';

const { Dragger } = Upload;
const { Text } = Typography;

const MAX_BYTES = 50 * 1000 * 1000; // 50MB

// Decimal MB to match macOS Finder
export const formatMB = (bytes: number): string =>
  `${(bytes / 1000 / 1000).toFixed(1)} MB`;

const jsonRequestPath = [DataFeedFormField.filters, 'jsonRequest'];

type Status = 'idle' | 'reading' | 'ready';

type JSONRequestInputProps = {
  value?: JSONRequest;
  onChange?: (value: JSONRequest | undefined) => void;
};

const JSONRequestInput = ({ value, onChange }: JSONRequestInputProps) => {
  const form = Form.useFormInstance();

  const [status, setStatus] = useState<Status>('idle');
  const [sourceName, setSourceName] = useState<string>('');

  const setFieldError = (errors: string[]) =>
    form.setFields([{ name: jsonRequestPath, errors }]);

  const load = async (input: File | string, source: string) => {
    setStatus('reading');
    setSourceName(source);

    try {
      const size =
        typeof input === 'string' ? new Blob([input]).size : input.size;
      if (size > MAX_BYTES) {
        onChange?.(undefined);
        setFieldError([
          'JSON is too large. It must be under 50 MB, minifying it may bring it under',
        ]);
        setStatus('idle');
        return;
      }
      let payload: string;
      if (typeof input === 'string') {
        await new Promise((resolve) => {
          setTimeout(resolve, 400);
        }); // simulate async reading when pasting to display action to user
        payload = input;
      } else {
        payload = await input.text();
      }
      onChange?.({ payload, sourceName: source, size });
      setFieldError([]);
      setStatus('ready');
    } catch {
      onChange?.(undefined);
      setFieldError(['Could not read the file.']);
      setStatus('idle');
    }
  };

  // Paste anywhere on the page loads the JSON, no dropzone click needed.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      // Skip pastes into other editable fields.
      const target = e.target as HTMLElement | null;
      if (
        target?.isContentEditable ||
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName ?? '')
      ) {
        return;
      }

      const file = e.clipboardData?.files?.[0];
      if (file) {
        e.preventDefault();
        load(file, file.name || 'pasted file');
        return;
      }
      const text = e.clipboardData?.getData('text');
      if (text) {
        e.preventDefault();
        load(text, 'pasted JSON');
      }
    };

    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, []);

  return (
    <div className="outline-none">
      <Dragger
        accept=".json,application/json"
        showUploadList={false}
        multiple={false}
        beforeUpload={(file: RcFile) => {
          load(file, file.name);
          return Upload.LIST_IGNORE; // handled locally
        }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">
          Drag a .json file, click to browse, or paste it (Ctrl+V).
        </p>
        <p className="ant-upload-hint">
          Must match the API definition. Max 50 MB.
        </p>
      </Dragger>

      <div className="mt-2">
        {status === 'reading' && (
          <span>
            <Spin size="small" className="mr-2" />
            <Text type="secondary">Reading {sourceName}…</Text>
          </span>
        )}
        {status === 'ready' && value && (
          <Alert
            type="success"
            showIcon
            message={`Loaded ${value.sourceName} — ${formatMB(value.size)}`}
          />
        )}
      </div>
    </div>
  );
};

export const JSONRequestFilters = ({ label }: { label?: string }) => (
  <Form.Item name={jsonRequestPath} label={label} hasFeedback>
    <JSONRequestInput />
  </Form.Item>
);
